import React from 'react';
import styles from './DeviceListCard.module.css';
import classnames from 'classnames';
import {
  Device, RealTimeDeviceData, Unit, Telemetry, ValveState, NotificationStats, DeviceType, Detector
} from 'model';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AlertsBadge } from 'components';

type DeviceItemBoxProps = {
  device:       Device,
  telemetry:    Telemetry,
  valveState:   ValveState | undefined,
  isConnected:  boolean,
  stats:        NotificationStats | undefined,
  isSelected:   boolean,
  onSelect:     (macAddress: string) => void,
}

const DeviceItemBox: React.FC<DeviceItemBoxProps> = React.memo(({
  device,
  telemetry,
  valveState = ValveState.CLOSED,
  isConnected,
  stats = { criticalCount: 0, infoCount: 0, warningCount: 0 },
  isSelected,
  onSelect
}) => {
  const { t } = useTranslation();
  return (
    <Row className={styles.smallerColPadding}>
      <Col onClick={() => onSelect(device.macAddress)} xs="12" className={classnames(
          styles.deviceItemContainer,
          { [styles.activeBorder]: isSelected }
        )}>
        <Row className={styles.deviceItemWrapper}>
          <Col xs="6">
            <div className={styles.deviceName}>{device.nickname}</div>
            <div className={styles.telemetryInfo}>
              { device.type === DeviceType.SHUTOFF ?
                 `${telemetry.flow.value} ${telemetry.flow.short}, ${telemetry.pressure.value} ${telemetry.pressure.short}, ${telemetry.temp.value} ${telemetry.temp.short}`
                 :
                 `${telemetry.humidity.value}${telemetry.humidity.short} RH, ${telemetry.battery.value}${telemetry.battery.short} BATT, ${telemetry.temp.value} ${telemetry.temp.short}`
              }
            </div>
          </Col>
          <Col xs="6" className={styles.deviceStateInfo}>
            <div
              className={classnames(
                { [styles.enabledOpened]: isConnected },
                { [styles.disabledClosed]: !isConnected },
              )}
            >
              {isConnected ? t('Device Online') : t('Device Offline')}
            </div>
            { device.type === DeviceType.SHUTOFF ?
              <div
                className={classnames(
                  { [styles.enabledOpened]: valveState === ValveState.OPEN },
                  { [styles.disabledClosed]: valveState === ValveState.CLOSED },
                )}
              >
                {valveState === ValveState.OPEN ? t('Valve Open') : t('Valve Closed')}
              </div>
              : (device as Detector).isDetectingWater &&
              <div className={styles.waterDetected}>{t('water-detected')}</div>
            }
          </Col>
          <div className={styles.badgeContainer}><AlertsBadge stats={stats} /></div>
        </Row>
      </Col>
    </Row>
  )
});

type DeviceListCardProps = {
  currentDevice?:   Device,
  realtimeMap:      { [macAddress: string]: RealTimeDeviceData },
  currentUnit?:     Unit,
  setCurrentDevice: (macAddress: string) => void,
}

export const DeviceListCard: React.FC<DeviceListCardProps> =  React.memo(({
  currentUnit,
  currentDevice,
  realtimeMap,
  setCurrentDevice,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Row className={styles.root}>
        <Col xs="12">
          <Row><Col xs="12"><div className={styles.divider}></div></Col></Row>
          <Row>
            <Col xs="12">
              <Row>
                <Col xs="12" className={classnames(styles.smallerColPadding, styles.listTitle)}>{`${currentUnit?.devices?.length} ${t('Devices')}`}</Col>
              </Row>

              <Row className={styles.listWrapperRow}>
                <Col xs="12" className={styles.listWrapperCol}>
                  { (currentUnit?.devices || []).map(
                    device => <DeviceItemBox
                      key         = {`item-box-${device.id}`}
                      device      = {device}
                      telemetry   = {device.type === DeviceType.SHUTOFF && realtimeMap[device.macAddress] ? realtimeMap[device.macAddress].telemetry : device.telemetry}
                      valveState  = {realtimeMap[device.macAddress] ? realtimeMap[device.macAddress].valve?.lastKnown : device.valve.lastKnown}
                      isConnected = {realtimeMap[device.macAddress] ? realtimeMap[device.macAddress].isConnected : device.isConnected}
                      // uncomment if realtime stats start to work fine:
                      //stats       = {realtimeMap[device.macAddress] ? realtimeMap[device.macAddress].notifications?.pending : device.notifications?.pending}
                      stats       = {device.notifications?.pending}
                      isSelected  = {device.macAddress === currentDevice?.macAddress}
                      onSelect    = { macAddress => setCurrentDevice(macAddress) }
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
});


export default DeviceListCard;
